/**
 * Feature flags for surfaces that exist in the codebase but aren't ready to ship.
 *
 * Flip a flag to re-enable everywhere it's gated. Keeping the code in place (just
 * hidden) avoids a churny delete/restore cycle for features that are paused, not
 * abandoned.
 */

/**
 * Remote Desktop (RDP-over-WebRTC). The host/control path isn't wired under the
 * CEF host yet (webrtcHost is inert), so every RDP entry point — the Add Panel
 * "Remote Desktop" tab, the MachineSelector screen-share button, the Settings
 * section, and the incoming-request consent dialog — is hidden until it's ready
 * to deploy. Flip to `true` to surface them again.
 */
export const RDP_ENABLED = false;

/**
 * Subscription plans. AtelierCode monetizes remote/orchestration infra (TURN,
 * multi-machine, team) — NOT AI tokens (users bring their own model/account).
 */
export type PlanTier = 'free' | 'pro' | 'team';

export interface PlanLimits {
  /** Max machines a user can connect/register for P2P remote. */
  maxMachines: number;
  /** Hosted TURN relays for reliable cross-NAT remote (Free = STUN-only). */
  turn: boolean;
  /** Team collaboration surfaces (shared queue, admin). */
  team: boolean;
}

export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: { maxMachines: 2, turn: false, team: false },
  pro: { maxMachines: Number.POSITIVE_INFINITY, turn: true, team: false },
  team: { maxMachines: Number.POSITIVE_INFINITY, turn: true, team: true },
};

/** Feature keys gated by plan (used by usePlan().can(...)). */
export type PlanFeature = 'turn' | 'team' | 'unlimitedMachines';

export function planAllows(plan: PlanTier, feature: PlanFeature): boolean {
  const limits = PLAN_LIMITS[plan];
  switch (feature) {
    case 'turn':
      return limits.turn;
    case 'team':
      return limits.team;
    case 'unlimitedMachines':
      return limits.maxMachines === Number.POSITIVE_INFINITY;
  }
}

/** Whether `plan` may connect one more machine given how many are already active. */
export function canAddMachine(plan: PlanTier, currentMachineCount: number): boolean {
  return currentMachineCount < PLAN_LIMITS[plan].maxMachines;
}

/**
 * Private-beta gate (ticket 102). While `true`, an authenticated user must carry
 * one of {@link BETA_ROLES} (a Keycloak realm role) to reach the app; everyone
 * else lands on the waitlist screen. Flip to `false` to open access for GA — the
 * gate code can then be deleted in one pass. Defaults `false` so the current
 * free beta is unaffected until we deliberately turn the gate on.
 */
export const BETA_GATE = false;

/** Keycloak realm roles that count as a beta invite. */
export const BETA_ROLES = ['beta', 'early-access'] as const;

/** Where the in-app "Send feedback" affordance points. */
export const FEEDBACK_EMAIL = 'feedback@ateliercode.dev';

/**
 * Whether a user with these realm roles may enter the app under the beta gate.
 * Returns `true` for everyone when {@link BETA_GATE} is off, so callers can use
 * it unconditionally. An invite = membership in {@link BETA_ROLES}.
 */
export function isBetaInvited(roles: string[] | undefined): boolean {
  if (!BETA_GATE) return true;
  if (!roles || roles.length === 0) return false;
  const invite: readonly string[] = BETA_ROLES;
  return roles.some((r) => invite.includes(r));
}
